/**
 * PB-Detail · Betreuungen-Tab
 *
 * Modern CRM 2.0 rebuild of the CRM 1.0 "Betreuungen" tab on /user/{id}.
 * Data stays on the existing legacy endpoint:
 *   GET /v1/admin/user/{id}/cares
 * The controller enriches every row with `bk_name`, `pb_name`, ratings,
 * matching (and origin, which the table no longer shows since 11.10.2026).
 * The partner name links to the partner's profile in a new tab. The tab
 * preserves CRM1 feature parity: annual timeline
 * with start/end visibility plus the care list and past/cancelled filter.
 *
 * YearTimeline/timeline helpers delegated to CareYearTimeline.tsx (shared
 * with PbDashboard — no duplication).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Badge, Button, Card, EditDrawer } from '../../../../components/ui';
import { BRAND, getThemeColors } from '../../../../lib/theme';
import { v1Users, v1Absences, type V1User, type V1Absence } from '../../../../lib/v1';
import { useIsMobile } from '../../../../lib/useBreakpoint';
import { CareEditForm } from '../../profile/CareEditForm';
import { JahresuebersichtCard } from '../../JahresuebersichtCard';
import { Rating } from '../../profile/controls';
import i18n from '../../../../i18n';
import {
  CareYearTimeline,
  STATUS_TONE,
  statusLabel,
  type CareRow,
} from '../../CareYearTimeline';
import { CareTableRow, CarePartnerLink, carePartnerHref, fmtDate, MatchingScore, Th } from './BetreuungenTabHelpers';
import { AbsenceCard, AbsenceForm } from './AbsenceForm';
import { TravelsCard } from '../../../betreuung/TravelsCard';

export { CreateCareModal } from './CreateCareModal';
export { AbsenceForm } from './AbsenceForm';

/**
 * Maximale Höhe der Einsatz-Liste: lange Listen scrollen in der Karte (Kopf
 * bleibt stehen), der Rest des Tabs über den Scroll-Balken der Seite.
 */
export const CARE_LIST_MAX_HEIGHT = 520;

/* ── Props ────────────────────────────────────────────────────────────────── */

interface Props {
  user: V1User | null;
  dark: boolean;
  /**
   * Eingebettet im Dashboard-Widget „Betreuungen" (Modus „erweitert"): Die
   * Jahresübersicht steht dort bereits einmal über dem Widget-Raster — ohne
   * dieses Flag erschienen Timeline und Betreuungen auf dem Dashboard doppelt.
   */
  hideTimeline?: boolean;
  /** Lädt Betreuungen/Abwesenheiten neu, wenn sich der Wert ändert. */
  reloadSignal?: number;
  /** Nach gespeicherten Änderungen an Betreuungen/Abwesenheiten in diesem Tab. */
  onChanged?: () => void;
  /**
   * Die API hat das Startdatum des PB automatisch fortgeschrieben (Betreuung
   * bestätigt bzw. bestätigte Betreuung storniert) — neuer Wert als
   * YYYY-MM-DD, damit die Seitenleiste des PB-Profils sofort nachzieht.
   */
  onPbStartdatumChanged?: (startdatum: string) => void;
}

export function BetreuungenTab({ user, dark, hideTimeline = false, reloadSignal = 0, onChanged, onPbStartdatumChanged }: Props) {
  const tc = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const isMobile = useIsMobile();
  // BK-Profil: statt PB-Abwesenheiten die Verfügbarkeit (Dispo) pflegen.
  const absenceVariant = user?.user_role === 1 ? 'bk' : 'pb';
  const [cares, setCares] = useState<CareRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Default true: zeigt alle Einsätze (inkl. abgeschlossene).
  // Mit false würde das Backend vergangene Einsätze ausblenden → leere Liste
  // für PBs ohne aktiven Einsatz (das war der ursprünglicher Bug).
  const [showOld, setShowOld] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [editCare, setEditCare] = useState<CareRow | null>(null);
  // Abwesenheiten (Punkt 5)
  const [absences, setAbsences] = useState<V1Absence[]>([]);
  const [absenceDraft, setAbsenceDraft] = useState<V1Absence | 'new' | null>(null);
  // Batch-12b: Reload-Signal für die gemeinsame JahresuebersichtCard —
  // wird nach Edits aus der Einsatz-Liste/AbsenceCard hochgezählt.
  const [ylReload, setYlReload] = useState(0);

  const reloadCares = useCallback(() => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    v1Users.cares(user.id, {
      perPage: 50,
      filters: { showOldCares: showOld ? 'true' : 'false' },
    })
      .then(p => { setCares((p.data ?? []) as CareRow[]); })
      .catch(e => { setError(e instanceof Error ? e.message : tr('pflege.cares.error_load')); })
      .finally(() => { setLoading(false); });
  }, [user?.id, showOld, tr]);

  const reloadAbsences = useCallback(() => {
    if (!user?.id) return;
    v1Absences.listForUser(user.id).then(setAbsences).catch(() => {/* timeline/table degrade to empty */});
  }, [user?.id]);


  useEffect(() => {
    reloadCares();
  }, [reloadCares, reloadSignal]);
  useEffect(() => {
    reloadAbsences();
  }, [reloadAbsences, reloadSignal]);

  return (
    // Natürliche Höhe statt `flex: 1; min-height: 0`: Der Tab-Container der
    // Detailseite scrollt selbst. Mit der alten Variante wurde der Inhalt in
    // die sichtbare Höhe gequetscht — die Einsatz-Liste schrumpfte auf 0 und
    // es gab keinen Scroll-Balken, um die Abwesenheiten darunter zu erreichen.
    <div data-testid="betreuungen-tab" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* ── Jahresübersicht — gemeinsame Karte (Batch-12b) ────────────── */}
      {!hideTimeline && (
        <JahresuebersichtCard
          user={user}
          collapsible
          collapseKey="betreuungen"
          dark={dark}
          reloadSignal={ylReload}
          onChanged={() => { reloadCares(); reloadAbsences(); }}
          onPbStartdatumChanged={onPbStartdatumChanged}
        />
      )}

      {/* ── Einsatz-Liste ─────────────────────────────────────────────── */}
      <Card dark={dark} style={{ padding: 0, display: 'flex', flexDirection: 'column', flexShrink: 0, overflow: 'hidden' }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 10, padding: '14px 16px', borderBottom: `1px solid ${tc.border}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: tc.heading }}>
              {tr('pflege.cares.count', { count: cares.length })}
            </h3>
            {loading && <span style={{ fontSize: 12, color: tc.muted }}>{tr('common.loading_short')}</span>}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: tc.text, cursor: 'pointer' }}>
            <input
              aria-label={tr('pflege.cares.show_past')}
              type="checkbox"
              checked={!showOld}
              onChange={e => setShowOld(!e.target.checked)}
              style={{ width: 18, height: 18, accentColor: BRAND, cursor: 'pointer' }}
            />
            {tr('pflege.cares.hide_past')}
          </label>
        </div>

        {error && (
          <div style={{ padding: 20, fontSize: 13, color: '#b42318' }}>{error}</div>
        )}

        {!error && isMobile ? (
          /* ── Mobile: gestapelte Karten ──────────────────────────────── */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {!loading && cares.length === 0 && (
              <div style={{ padding: 32, textAlign: 'center', color: tc.muted, fontSize: 13 }}>{tr('pflege.cares.empty')}</div>
            )}
            {cares.map(c => {
              const cc = c as CareRow & { matching?: number | null; pb_rating?: number | null; bk_rating?: number | null };
              const partnerName = user?.user_role === 2 ? c.bk_name : c.pb_name;
              const partnerId = user?.user_role === 2 ? c.bk_user_id : c.pb_user_id;
              const partnerHref = carePartnerHref(partnerId, user?.user_role === 2);
              const rating = user?.user_role === 2 ? cc.pb_rating : cc.bk_rating;
              const stTone = STATUS_TONE[c.status] ?? 'neutral';
              const stLabel = statusLabel(c.status, (k: string, opts?: Record<string, unknown>) => i18n.t(k, opts));
              return (
                <div
                  key={c.id}
                  role="button"
                  tabIndex={0}
                  aria-label={tr('pflege.cares.row_aria', { id: c.id, name: partnerName ?? '' })}
                  onClick={() => setEditCare(c)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditCare(c); } }}
                  style={{
                    padding: '14px 16px',
                    borderBottom: `1px solid ${tc.borderSoft}`,
                    cursor: 'pointer',
                    background: c.status === 1 ? tc.cardMuted : undefined,
                  }}
                >
                  {/* Header: Status + Partner */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <Badge dark={dark} tone={stTone}>{stLabel}</Badge>
                    <span style={{ fontSize: 10, color: tc.muted }}>✏ {tr('pflege.cares.edit_hint')}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <Avatar name={partnerName ?? '?'} userId={partnerId ?? null} size={36} tone={user?.user_role === 2 ? 'green' : 'blue'} />
                    <div>
                      <div style={{ fontWeight: 650, color: tc.heading, fontSize: 14 }}><CarePartnerLink name={partnerName} href={partnerHref} color={tc.heading} /></div>
                      {partnerId ? <div style={{ fontSize: 11, color: tc.muted }}>#{partnerId}</div> : null}
                    </div>
                  </div>
                  {/* Daten-Grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 10, color: tc.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{tr('pflege.cares.th.start')}</div>
                      <div style={{ fontSize: 13, color: tc.heading, fontWeight: 500, marginTop: 2 }}>{fmtDate(c.startdatum)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: tc.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{tr('pflege.cares.th.end')}</div>
                      <div style={{ fontSize: 13, color: tc.heading, fontWeight: 500, marginTop: 2 }}>{fmtDate(c.enddatum)}</div>
                    </div>
                    {cc.matching != null && (
                      <div>
                        <div style={{ fontSize: 10, color: tc.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{tr('pflege.cares.th.matching')}</div>
                        <div style={{ marginTop: 2 }}><MatchingScore value={cc.matching ?? null} dark={dark} /></div>
                      </div>
                    )}
                    {rating != null && (
                      <div>
                        <div style={{ fontSize: 10, color: tc.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{tr('pflege.cares.th.rating')}</div>
                        <div style={{ marginTop: 2 }}><Rating dark={dark} value={Number(rating)} /></div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : !error ? (
          /* ── Desktop: Tabelle ───────────────────────────────────────── */
          <div data-testid="betreuungen-list-scroll" style={{ overflow: 'auto', maxHeight: CARE_LIST_MAX_HEIGHT }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 820 }}>
              <thead style={{ position: 'sticky', top: 0, background: tc.tableHeader, zIndex: 1 }}>
                <tr>
                  <Th dark={dark}>{tr('pflege.cares.th.status')}</Th>
                  <Th dark={dark}>{tr('pflege.cares.th.start')}</Th>
                  <Th dark={dark}>{tr('pflege.cares.th.end')}</Th>
                  <Th dark={dark}>{user?.user_role === 2 ? tr('pflege.cares.th.caregiver') : tr('pflege.cares.th.care_recipient')}</Th>
                  <Th dark={dark}>{tr('pflege.cares.th.matching')}</Th>
                  <Th dark={dark}>{tr('pflege.cares.th.rating')}</Th>
                </tr>
              </thead>
              <tbody>
                {!loading && cares.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ padding: 32, textAlign: 'center', color: tc.muted, fontSize: 13 }}>
                      {tr('pflege.cares.empty')}
                    </td>
                  </tr>
                )}
                {cares.map(c => (
                  <CareTableRow
                    key={c.id}
                    care={c}
                    user={user}
                    dark={dark}
                    onEdit={() => setEditCare(c)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>

      {/* ── Abwesenheiten-Karte (Punkt 5) ─────────────────────────────── */}
      <AbsenceCard
        absences={absences}
        dark={dark}
        variant={absenceVariant}
        onAdd={() => setAbsenceDraft('new')}
        onEdit={a => setAbsenceDraft(a)}
        onDeleted={() => { reloadAbsences(); setYlReload(x => x + 1); onChanged?.(); }}
      />

      {/* ── Reiseorganisation (nur BK, wie CRM 1.0) ───────────────────── */}
      {user?.user_role === 1 && user.id && !hideTimeline && <TravelsCard user={user} dark={dark} />}

      {/* Edit Drawer */}
      <EditDrawer
        open={editCare !== null}
        onClose={() => setEditCare(null)}
        title={tr('pflege.detail.dashboard.betreuungen')}
        dark={dark}
      >
        {editCare && user?.id && (
          <CareEditForm
            dark={dark}
            care={{ ...editCare, pb_user_id: editCare.pb_user_id ?? user.id }}
            // Nur im PB-Profil: dort ist `user` der PB, dessen Startdatum sich ändert.
            onPbStartdatumChanged={user.user_role === 1 ? undefined : onPbStartdatumChanged}
            onSaved={() => {
              setEditCare(null);
              reloadCares();
              setYlReload(x => x + 1);
              onChanged?.();
            }}
            onCancel={() => setEditCare(null)}
          />
        )}
      </EditDrawer>

      {/* Abwesenheit anlegen/bearbeiten (Punkt 5) */}
      <EditDrawer
        open={absenceDraft !== null}
        onClose={() => setAbsenceDraft(null)}
        title={absenceVariant === 'bk'
          ? (absenceDraft && absenceDraft !== 'new' ? tr('betreuung.availability.edit_title') : tr('betreuung.availability.add'))
          : (absenceDraft && absenceDraft !== 'new' ? tr('pflege.absences.edit_title') : tr('pflege.absences.add'))}
        dark={dark}
      >
        {absenceDraft !== null && user?.id && (
          <AbsenceForm
            dark={dark}
            variant={absenceVariant}
            userId={user.id}
            initial={absenceDraft === 'new' ? null : absenceDraft}
            onSaved={() => { setAbsenceDraft(null); reloadAbsences(); setYlReload(x => x + 1); onChanged?.(); }}
            onCancel={() => setAbsenceDraft(null)}
          />
        )}
      </EditDrawer>

    </div>
  );
}
